import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';

export interface SignerConfiguration {
    id: number;
    name: string;
    value: string;
    label: string;
}

export interface Certificate {
    slug: string;
    name: string;
    description: string;
    rate_limit_per_hour: number;
    enabled: boolean;
    rate_limit_status?: {
        limit: number;
        remaining: number;
        reset_at: string;
    };
    logo_status?: {
        has_logo: boolean;
        enabled: boolean;
    };
}

export interface LogoStatusMap {
    [slug: string]: {
        has_logo: boolean;
        enabled: boolean;
    };
}

export interface HealthResponse {
    success: boolean;
    data?: {
        status: string;
        service: string;
        version: string;
    };
    error?: string;
}

export interface ReadyResponse {
    success: boolean;
    data?: {
        status: string;
        components: {
            database: string;
            redis: string;
            disk: {
                status: string;
                free_mb: number;
            };
        };
    };
    error?: string;
}

export interface ConfigurationResponse {
    configurations: SignerConfiguration[];
    watermark_preview: string;
}

@Injectable({
    providedIn: 'root',
})
export class SignSettingsConfigurationService {
    private token!: string;
    private basePath = environment.interop.basePath;

    private healthUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.administrative.signer.health;

    private readyUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.administrative.signer.ready;

    private certificatesUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.administrative.signer.certificates;

    private certificateBySlugUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.administrative.signer.certificateBySlug;

    private rateLimitUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.administrative.signer.rateLimit;

    private configurationUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.administrative.signer.configuration;

    private testConnectionUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.administrative.signer.testConnection;

    private testSignUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.administrative.signer.testSign;

    private syncCertificatesUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.administrative.signer.syncCertificates;

    private watermarkPreviewUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.administrative.signer.watermarkPreview;

    private downloadFileUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.administrative.signer.downloadFile;

    // Logo URLs
    private logoStatusUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.administrative.signer.logoStatus;

    private logoBaseUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.administrative.signer.uploadLogo;

    private httpOptions: any;

    constructor(private http: HttpClient) { }

    getHealth(): Observable<any> {
        return this.http.get(this.healthUrl, this.httpOptions);
    }

    getReady(): Observable<any> {
        return this.http.get(this.readyUrl, this.httpOptions);
    }

    getCertificates(): Observable<any> {
        return this.http.get(this.certificatesUrl, this.httpOptions);
    }

    getCertificateBySlug(slug: string): Observable<any> {
        return this.http.get(this.certificateBySlugUrl + slug, this.httpOptions);
    }

    getRateLimit(slug: string): Observable<any> {
        return this.http.get(this.rateLimitUrl + slug, this.httpOptions);
    }

    getConfiguration(): Observable<any> {
        return this.http.get(this.configurationUrl, this.httpOptions);
    }

    updateConfiguration(configurations: any[]): Observable<any> {
        return this.http.put(this.configurationUrl, { configurations }, this.httpOptions);
    }

    testConnection(): Observable<any> {
        return this.http.post(this.testConnectionUrl, {}, this.httpOptions);
    }

    testSign(slug: string): Observable<any> {
        return this.http.post(this.testSignUrl + slug, {}, this.httpOptions);
    }

    syncCertificates(): Observable<any> {
        return this.http.post(this.syncCertificatesUrl, {}, this.httpOptions);
    }

    getWatermarkPreview(): Observable<any> {
        return this.http.get(this.watermarkPreviewUrl, this.httpOptions);
    }

    downloadSignedFile(requestId: string): Observable<Blob> {
        return this.http.get(this.downloadFileUrl + requestId, {
            responseType: 'blob',
        });
    }

    setToken(token: string) {
        this.token = token;
        this.httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: `Bearer ${this.token}`,
            }),
        };
    }

    // ==================== LOGO METHODS ====================

    getLogoStatus(): Observable<any> {
        return this.http.get(this.logoStatusUrl, this.httpOptions);
    }

    uploadLogo(slug: string, file: File): Observable<any> {
        const formData = new FormData();
        formData.append('logo', file);
        // Don't use httpOptions with Content-Type for FormData
        return this.http.post(`${this.logoBaseUrl}${slug}/logo`, formData);
    }

    deleteLogo(slug: string): Observable<any> {
        return this.http.delete(`${this.logoBaseUrl}${slug}/logo`, this.httpOptions);
    }

    toggleLogo(slug: string, enabled: boolean): Observable<any> {
        return this.http.put(`${this.logoBaseUrl}${slug}/logo-enabled`, { enabled }, this.httpOptions);
    }

    getLogo(slug: string): Observable<Blob> {
        return this.http.get(`${this.logoBaseUrl}${slug}/logo`, {
            responseType: 'blob',
        });
    }

    getLogoUrl(slug: string): string {
        return `${this.logoBaseUrl}${slug}/logo`;
    }
}
